import { useQuery } from "@tanstack/react-query";
import moment from "moment/moment";
import qs from "qs";
import { useParams } from "react-router-dom";
import { getSearchParams } from "search-params";
import { get } from "./axios-base";
import { DayState, DayStateStatus, ExperienceGroupSizeLimit } from "types/day-state";
import { dateToString } from "utils/moment-utils";

interface DaySectionData {
    available: number[];
    waitList: number[];
    open: boolean;
    activeExperienceIds: number[];
    minGroupSize: number | null;
    maxGroupSize: number | null;
    experienceGroupSizeLimits: ExperienceGroupSizeLimit[];
    onlyTextRows: boolean;
}

interface DayData {
    available: number[];
    waitList: number[];
    open: boolean;
    bySection: { [sectionId: number]: DaySectionData };
}

interface Days {
    [n: string]: DayData;
}
interface DayStatesParams {
    includeAvailableSeats: boolean;
    includeFirstAvailableMonth: boolean;
    fromDate: string;
    toDate: string;
    unitId: string;
    sectionId?: number[];
    tag?: string;
    invite?: string;
    isBookingRequest: boolean;
}
/**
 * useDayStates stores the data for `date`'s month under the NEXT month's
 * "YYYY-MM" (the params computation mutates its moment — long-standing quirk).
 */
export function getDayStatesMonthKey(date: Date): string {
    return moment(date).startOf("month").add(1, "month").format("YYYY-MM");
}

/**
 * The query key includes the booking-request mode because the backend returns
 * different day states for the same month in that mode (BOOK-3218). Every cache
 * reader must build its key through this helper so the shape stays in sync.
 */
/**
 * Includes the invitation (VIP) link token, as the response depends on it: without it a month cached
 * for a normal visit would serve a VIP one, and the other way round. Read here so every cache reader
 * builds the same key.
 */
export function getDayStatesQueryKey(date: Date, bookingRequestMode: boolean) {
    return [
        "dayStates",
        getDayStatesMonthKey(date),
        bookingRequestMode,
        getSearchParams().invite || null,
    ] as const;
}

export function useDayStates(
    date: Date,
    bookingRequestMode: boolean = false,
    enabled: boolean = true
) {
    const { unitId } = useParams();
    const { sectionIds, tag, invite } = getSearchParams();
    const m = moment(date);

    const startOfMonth = m.startOf("month");
    const today = moment().startOf("day");
    const fromDate = startOfMonth.isAfter(today) ? startOfMonth : today;

    const params: DayStatesParams = {
        // do not include available seats when in booking request mode
        includeAvailableSeats: !bookingRequestMode,
        includeFirstAvailableMonth: !bookingRequestMode,
        fromDate: dateToString(fromDate),
        toDate: dateToString(m.add(1, "month").startOf("month")),
        unitId: unitId || "",
        // a day is only Open when it has an Inquiry-type rule, so the calendar can
        // correctly strike through days with no request rule (BOOK-3218)
        isBookingRequest: bookingRequestMode,
    };

    if (sectionIds) {
        params.sectionId = sectionIds;
    }

    if (tag) {
        params.tag = tag;
    }

    if (invite) {
        params.invite = invite;
    }

    return useQuery({
        enabled,
        queryKey: getDayStatesQueryKey(date, bookingRequestMode),
        queryFn: async () => {
            const response = await get(`/WebBooking/dayStates`, {
                params,
                paramsSerializer: (params: unknown) => {
                    return qs.stringify(params, { arrayFormat: "repeat" });
                },
                transformResponse: (data: unknown) => {
                    const days: Days = {};
                    const payload = JSON.parse(data as string) as DayState[];
                    payload.forEach((dayState) => {
                        const key = moment(dayState.day).format("YYYY-MM-DD");
                        if (!days[key])
                            days[key] = { available: [], waitList: [], open: false, bySection: {} };

                        const day = days[key];

                        if (dayState.availableSeats) day.available.push(...dayState.availableSeats);
                        if (dayState.waitListSeats) day.waitList.push(...dayState.waitListSeats);
                        if (dayState.state === DayStateStatus.Open) day.open = true;

                        const sId = dayState.sectionId;
                        if (!day.bySection[sId])
                            day.bySection[sId] = {
                                available: [],
                                waitList: [],
                                open: false,
                                activeExperienceIds: [],
                                minGroupSize: null,
                                maxGroupSize: null,
                                experienceGroupSizeLimits: [],
                                onlyTextRows: false,
                            };
                        const section = day.bySection[sId];
                        if (dayState.availableSeats)
                            section.available.push(...dayState.availableSeats);
                        if (dayState.waitListSeats)
                            section.waitList.push(...dayState.waitListSeats);
                        if (dayState.state === DayStateStatus.Open) section.open = true;
                        if (dayState.activeExperienceIds)
                            section.activeExperienceIds.push(...dayState.activeExperienceIds);
                        section.minGroupSize = dayState.minGroupSize ?? null;
                        section.maxGroupSize = dayState.maxGroupSize ?? null;
                        section.onlyTextRows = dayState.onlyTextRows ?? false;
                        if (dayState.experienceGroupSizeLimits)
                            section.experienceGroupSizeLimits.push(
                                ...dayState.experienceGroupSizeLimits
                            );
                    });

                    return days;
                },
            });
            return response.data as Days;
        },
    });
}
export function getAvailableSectionIds(days: Days, dateKey: string, guests: number): Set<number> {
    const dayData = days[dateKey];
    if (!dayData) return new Set();
    return new Set(
        Object.entries(dayData.bySection)
            .filter(([, data]) => data.open && data.available.includes(guests))
            .map(([sectionId]) => Number(sectionId))
    );
}

export function getAvailableExperienceIds(
    days: Days,
    dateKey: string,
    guests: number
): Set<number> {
    const dayData = days[dateKey];
    if (!dayData) return new Set();
    // A text-row-only section can only carry information-only experiences, which have no seats.
    const ids = Object.values(dayData.bySection)
        .filter((data) => data.open && (data.available.includes(guests) || data.onlyTextRows))
        .flatMap((data) => data.activeExperienceIds);
    return new Set(ids);
}

/**
 * Text rows carry a message instead of times, so a day offering nothing else is neither available
 * nor fully booked: it gets no dot and stays selectable (BOOK-3969).
 */
function hasOnlyTextRows(sections: DaySectionData[]): boolean {
    return sections.length > 0 && sections.every((section) => section.onlyTextRows);
}

export function getNonAvailableDatesForGuests(days: Days, guests: number) {
    const closedDays: Date[] = [];
    const waitListDays: Date[] = [];
    const fullyBookedDays: Date[] = [];

    Object.entries(days).forEach(([key, { available, waitList, open, bySection }]) => {
        if (!open) {
            closedDays.push(new Date(key));
        } else if (
            !hasOnlyTextRows(Object.values(bySection).filter((section) => section.open)) &&
            !available.includes(guests)
        ) {
            if (waitList.includes(guests)) {
                waitListDays.push(new Date(key));
            } else {
                fullyBookedDays.push(new Date(key));
            }
        }
    });
    return { closedDays, waitListDays, fullyBookedDays };
}

export function getNonAvailableDatesForSection(days: Days, sectionId: number, guests: number) {
    const closedDays: Date[] = [];
    const waitListDays: Date[] = [];
    const fullyBookedDays: Date[] = [];

    Object.entries(days).forEach(([key, dayData]) => {
        const section = dayData.bySection[sectionId];
        if (!section?.open) {
            closedDays.push(new Date(key));
        } else if (!section.onlyTextRows && !section.available.includes(guests)) {
            if (section.waitList.includes(guests)) {
                waitListDays.push(new Date(key));
            } else {
                fullyBookedDays.push(new Date(key));
            }
        }
    });
    return { closedDays, waitListDays, fullyBookedDays };
}

export function getNonAvailableDatesForExperience(
    days: Days,
    experienceId: number,
    guests: number
) {
    const closedDays: Date[] = [];
    const waitListDays: Date[] = [];
    const fullyBookedDays: Date[] = [];

    Object.entries(days).forEach(([key, dayData]) => {
        const activeSections = Object.values(dayData.bySection).filter(
            (section) => section.open && section.activeExperienceIds.includes(experienceId)
        );
        if (activeSections.length === 0) {
            closedDays.push(new Date(key));
        } else if (
            !hasOnlyTextRows(activeSections) &&
            !activeSections.some((s) => s.available.includes(guests))
        ) {
            if (activeSections.some((s) => s.waitList.includes(guests))) {
                waitListDays.push(new Date(key));
            } else {
                fullyBookedDays.push(new Date(key));
            }
        }
    });
    return { closedDays, waitListDays, fullyBookedDays };
}

export function getGroupSizeLimitsForDate(
    days: Days,
    dateKey: string,
    experienceId?: number,
    sectionId?: number
): { min: number | null; max: number | null } {
    const dayData = days[dateKey];
    if (!dayData) return { min: null, max: null };

    if (sectionId !== undefined) {
        const section = dayData.bySection[sectionId];
        return { min: section?.minGroupSize ?? null, max: section?.maxGroupSize ?? null };
    }

    if (experienceId !== undefined) {
        const activeSections = Object.values(dayData.bySection).filter(
            (s) => s.open && s.activeExperienceIds.includes(experienceId)
        );
        const limits = activeSections.flatMap((s) =>
            s.experienceGroupSizeLimits.filter((l) => l.experienceId === experienceId)
        );
        if (limits.length === 0) return { min: null, max: null };
        const anyNullMin = limits.some((l) => l.minGroupSize === null);
        const anyNullMax = limits.some((l) => l.maxGroupSize === null);
        const mins = limits.map((l) => l.minGroupSize!);
        const maxes = limits.map((l) => l.maxGroupSize!);
        return {
            min: anyNullMin ? null : Math.min(...mins),
            max: anyNullMax ? null : Math.max(...maxes),
        };
    }

    const openSections = Object.values(dayData.bySection).filter((s) => s.open);
    if (openSections.length === 0) return { min: null, max: null };
    const anyNullMin = openSections.some((s) => s.minGroupSize === null);
    const anyNullMax = openSections.some((s) => s.maxGroupSize === null);
    const mins = openSections.map((s) => s.minGroupSize!);
    const maxes = openSections.map((s) => s.maxGroupSize!);
    return {
        min: anyNullMin ? null : Math.min(...mins),
        max: anyNullMax ? null : Math.max(...maxes),
    };
}

export type { Days };
